import moment from 'moment';
import { dimensionObj, ORDER_DOCUMENT_TYPE, COLUMN_CLASSNAME_MAP, COLUMN_TRANSLATION_MAP } from 'src/app/Orders/order.const';
import { withItemsVerb } from 'src/app/Orders/OrderStatus/nba.helpers';
import { getDynamicPickupDateDisplay } from 'src/app/Orders/OrderStatus/pickupDate.helpers';
import { MEASUREMENT, ORDER_STATUS } from 'src/constants/common';
import { BadgeType } from 'src/interface/common';
import { getDateWithSuffixFormat, getFormattedDateWithTime, getPriorityDeliveryDateTime, getPriorityPickupDateTime } from 'src/utils/CommonFunctions';
import * as Yup from 'yup';

// ─── Dashboard helpers (custom views & draggable columns) ─────────────────────

export interface IOrderTab {
    value: string;
    name: string;
    count?: number;
    id?: number;
    appliedFilters?: {
        search: string;
        member: string;
        startDate: string | null;
        endDate: string | null;
        status: string;
        classification: string | null;
        shipper: string | null;
        consignee: string | null;
    };
    appliedSorting?: {
        sortType: 'asc' | 'desc';
        sortField: string;
    };
    isIconShow?: boolean;
}

/**
 * Transforms column config array into headCells format for TableCmp.
 * Ensures 'action' column is always last and applies translations + CSS classes.
 */
export const transformColumnsToHeadCells = (defaultColumns: any[], hasData: boolean, t: (key: string) => string) => {
    const sortedColumns = [...defaultColumns].sort((a, b) => a.displayOrder - b.displayOrder);

    const actionIndex = sortedColumns.findIndex((col) => col.id === 'action');

    if (actionIndex !== -1) {
        const actionColumn = sortedColumns.splice(actionIndex, 1)[0];
        sortedColumns.push(actionColumn);
    }

    return sortedColumns.map((column) => {
        const isActionColumn = column.id === 'action';
        const headCell: any = {
            ...column,
            sortable: isActionColumn ? false : hasData,
            rowClassName: COLUMN_CLASSNAME_MAP[column.id] || '',
        };

        if (!isActionColumn) {
            const translationKey = COLUMN_TRANSLATION_MAP[column.id];
            headCell.name = translationKey ? t(translationKey) : column.name;
        }

        return headCell;
    });
};

/**
 * Transforms API response array of views into IOrderTab format for tab rendering.
 */
export const formateTabData = (payloadArray: any[]): IOrderTab[] =>
    payloadArray.map((item) => ({
        name: item.name,
        value: item.id?.toString(),
        id: item?.id,
        count: item.count ?? 0,
        appliedFilters: item.appliedFilters,
        appliedSorting: item.appliedSorting,
        isIconShow: true,
    }));

/**
 * Extracts unique view names from tabs array for duplicate validation.
 */
export const extractViewNames = (tabsArray: IOrderTab[]): string[] => {
    const names = tabsArray.map((tab) => tab?.name).filter(Boolean);

    return Array.from(new Set(names));
};

/**
 * Formats the first row of order data for DraggableLayout column preview.
 */
export const formatRowData = (rowData: any[]) => {
    if (!rowData.length) return [];

    const rowArray = rowData.slice(0, 1);

    return rowArray.map((order) => [
        {
            id: 'orderId',
            name: 'Order #',
            data: {
                orderId: order?.orderId,
                isPriority: order?.isPriority,
            },
            boxWrapClassName: 'max-w-[13rem]',
        },
        {
            id: 'salesRep',
            name: 'Created By',
            data: {
                userData: order?.userData,
            },
            boxWrapClassName: 'max-w-[165px]',
        },
        {
            id: 'orderStatus',
            name: 'Status',
            data: { orderStatus: order?.orderStatus },
        },
        {
            id: 'orderType',
            name: 'Type',
            data: { orderType: order?.orderType },
            boxWrapClassName: 'max-w-[150px]',
        },
        {
            id: 'shipperFullAddress',
            name: 'Origin',
            data: {
                originAddress: order?.originAddress,
            },
            boxWrapClassName: 'max-w-[300px]',
        },
        {
            id: 'consigneeFullAddress',
            name: 'Destination',
            data: {
                destinationAddress: order?.destinationAddress,
            },
            boxWrapClassName: 'max-w-[300px]',
        },
        {
            id: 'createdAt',
            name: 'Created',
            data: { createdAt: order?.createdAt },
            boxWrapClassName: 'max-w-[165px]',
        },
        {
            id: 'handlingUnits',
            name: 'Handling Unit',
            data: {
                handlingUnits: order?.handlingUnits,
                orderType: order?.orderType,
                handlingUnitTypes: order?.handlingUnitTypes,
                primaryEquipmentTypeFullName: order?.primaryEquipmentTypeFullName,
            },
        },
        {
            id: 'totalWeight',
            name: 'Total Weight',
            data: {
                totalWeight: order?.totalWeightRaw,
                weightMeasurement: order?.weightMeasurement,
            },
        },
        {
            id: 'totalCost',
            name: 'Cost',
            data: {
                totalCost: order?.totalCost,
                currency: order?.currency,
            },
        },
        {
            id: 'updatedAt',
            name: 'Last Modified',
            data: { updatedAt: order?.updatedAt },
            boxWrapClassName: 'max-w-[165px]',
        },
        {
            id: 'pickupDate',
            name: 'Pickup Date',
            data: { pickupDate: order?.pickupDate },
            boxWrapClassName: 'max-w-[155px]',
        },
        {
            id: 'deliveryDate',
            name: 'Delivery Date',
            data: { deliveryDate: order?.deliveryDate },
            boxWrapClassName: 'max-w-[155px]',
        },
        {
            id: 'carrier',
            name: 'Carrier',
            data: { carrierData: order?.carrierData },
            boxWrapClassName: 'max-w-[165px]',
        },
        {
            id: 'bolNumber',
            name: 'BOL #',
            data: { bolNumber: order?.bolNumber },
            boxWrapClassName: 'max-w-[145px]',
        },
        {
            id: 'refNumber',
            name: 'Reference #',
            data: { refNumber: order?.refNumber },
            boxWrapClassName: 'max-w-[145px]',
        },
        {
            id: 'linearFootage',
            name: 'Linear Feet',
            data: { linearFootage: order?.linearFootage },
            boxWrapClassName: 'max-w-[130px]',
        },
        {
            id: 'equipmentType',
            name: 'Equipment',
            data: { equipmentTypeFullName: order?.equipmentTypeFullName, equipmentType: order?.equipmentType },
            boxWrapClassName: 'max-w-[145px]',
        },
    ]);
};

/**
 * Checks if the current params have changed compared to a saved view's filters/sorting.
 */
export const isOrderParamsChanged = (viewData: any, currentParams: any): boolean => {
    if (!viewData?.appliedFilters) return false;

    const filters = viewData.appliedFilters;
    const sorting = viewData.appliedSorting;

    const fieldsChanged =
        (filters.search || '') !== (currentParams.search || '') ||
        (filters.status || 'all') !== (currentParams.status || 'all') ||
        (filters.classification || null) !== (currentParams.classification || null) ||
        (filters.member || 'all') !== (currentParams.member || 'all') ||
        (filters.shipper || null) !== (currentParams.shipper || null) ||
        (filters.consignee || null) !== (currentParams.consignee || null);

    const datesChanged = (() => {
        const normalizeDate = (val: any) => (val === '' || val === null || val === undefined ? null : val);

        return normalizeDate(filters.startDate) !== normalizeDate(currentParams.startDate) || normalizeDate(filters.endDate) !== normalizeDate(currentParams.endDate);
    })();

    const sortChanged = (sorting?.sortType || 'desc') !== (currentParams.sortType || 'desc') || (sorting?.sortField || 'createdAt') !== (currentParams.sortField || 'createdAt');

    return fieldsChanged || datesChanged || sortChanged;
};

export const constructOrderParam = (masterOrder: any, id: number, weightMeasurement: string, lengthMeasurement: string) => ({
    shipperCompanyName: masterOrder?.shipper?.companyName,
    shipperContactName: masterOrder?.shipper?.contactName,
    shipperContactNumber: masterOrder?.shipper?.phoneNumber,
    shipperEmail: masterOrder?.shipper?.email,
    shipperFullAddress: masterOrder?.shipper?.shipperAddress?.fullAddress,
    shipperAddress1: masterOrder?.shipper?.shipperAddress?.address1,
    shipperCity: masterOrder?.shipper?.shipperAddress?.city,
    shipperState: masterOrder?.shipper?.shipperAddress?.state,
    shipperCountry: masterOrder?.shipper?.shipperAddress?.country,
    shipperPostal: masterOrder?.shipper?.shipperAddress?.postal,
    shipperLatitude: masterOrder?.shipper?.shipperAddress?.latitude,
    shipperLongitude: masterOrder?.shipper?.shipperAddress?.longitude,
    shipperStateCode: masterOrder?.shipper?.shipperAddress?.stateCode,
    shipperCountryCode: masterOrder?.shipper?.shipperAddress?.countryCode,
    consigneeCompanyName: masterOrder?.consignee?.companyName,
    consigneeContactName: masterOrder?.consignee?.contactName,
    consigneeContactNumber: masterOrder?.consignee?.phoneNumber,
    consigneeEmail: masterOrder?.consignee?.email,
    consigneeFullAddress: masterOrder?.consignee?.consigneeAddress?.fullAddress,
    consigneeAddress1: masterOrder?.consignee?.consigneeAddress?.address1,
    consigneeCity: masterOrder?.consignee?.consigneeAddress?.city,
    consigneeState: masterOrder?.consignee?.consigneeAddress?.state,
    consigneeCountry: masterOrder?.consignee?.consigneeAddress?.country,
    consigneePostal: masterOrder?.consignee?.consigneeAddress?.postal,
    consigneeLatitude: masterOrder?.consignee?.consigneeAddress?.latitude,
    consigneeLongitude: masterOrder?.consignee?.consigneeAddress?.longitude,
    consigneeStateCode: masterOrder?.consignee?.consigneeAddress?.stateCode,
    consigneeCountryCode: masterOrder?.consignee?.consigneeAddress?.countryCode,
    id,
    order_dimensions: masterOrder?.order_dimensions,
    declaredValue: masterOrder?.declaredValue,
    declaredValueUnit: masterOrder?.declaredValueUnit,
    equipmentType: masterOrder?.equipmentType,
    equipmentTypeFullName: masterOrder?.equipmentTypeFullName,
    linearFootage: masterOrder?.linearFootage,
    orderType: masterOrder?.orderType,
    poNumber: masterOrder?.poNumber,
    refNumber: masterOrder?.refNumber,
    specialNotes: masterOrder?.specialNotes,
    weightMeasurement,
    lengthMeasurement,
    additionalServices: masterOrder?.additionalServices,
    carrierPrice: masterOrder?.carrierPrice,
    salesPrice: masterOrder?.salesPrice,
});

export const filterServicesByType = (services: any, selectedServices: any, type: any) => {
    if (!Array.isArray(services) || services.length === 0) return [];
    if (!Array.isArray(selectedServices) || selectedServices.length === 0) return [];

    const filteredService = services.filter((service) => service.type === type && selectedServices.includes(service.id));

    return filteredService;
};

// ─── Special instruction mention markup ──────────────────────────────────────
// Order instructions are authored in WAL with an `@[variableId]` mention editor and
// stored as markup, so they must be resolved at render time — an accessorial or quote
// added after the instruction was written still fills in. Mirrors
// resolveSpecialInstructionText / getSpecialInstructionValues in wal-frontend-v2
// src/app/Orders/order.helpers.ts; the labels below must match WAL byte-for-byte or the
// portal and the carrier's copy of the same instruction read differently.

// Every variable resolves onto its own labelled line, exactly as on the WAL side.
const labelledLine = (label: string, value: string): string => (value ? `\n${label}: ${value}` : '');

/**
 * Names of the order's additional services of one type, for the `@[*Additionals]`
 * variables. Takes the radar detail's flat `additionalServices` rows (which already carry
 * serviceName/serviceCategory) rather than the global services list + serviceId array the
 * Orders module uses, because that is the only shape the radar endpoint returns.
 *
 * Deliberately NOT filtered by `orderLegId`: with Show-as-Multi-Leg off the presented legs
 * are consolidated spans, so a row's orderLegId can point at a leg the customer never
 * sees. Same all-rows treatment as the Track Order Details section.
 */
const getAdditionalServiceNames = (additionalServices: any, type: 'PICKUP' | 'DELIVERY' | 'OTHER'): string =>
    (Array.isArray(additionalServices) ? additionalServices : [])
        .filter((service: any) => service?.serviceCategory === type || service?.serviceType === type)
        .slice()
        .sort((a: any, b: any) => (a?.serviceId ?? 0) - (b?.serviceId ?? 0))
        .map((service: any) => service?.serviceName)
        .filter(Boolean)
        .join(', ');

export const getSpecialInstructionValues = ({ quoteNumber, additionalServices }: { quoteNumber?: string | null; additionalServices?: any }): Record<string, string> => ({
    quoteNumber: labelledLine('Quote ID', quoteNumber || ''),
    pickupAdditionals: labelledLine('Pickup Service', getAdditionalServiceNames(additionalServices, 'PICKUP')),
    deliveryAdditionals: labelledLine('Delivery Service', getAdditionalServiceNames(additionalServices, 'DELIVERY')),
    otherAdditionals: labelledLine('Other Service', getAdditionalServiceNames(additionalServices, 'OTHER')),
});

/**
 * Substitute `@[variableId]` markup with its resolved value. An unknown or empty variable
 * collapses to blank so raw markup can never reach the customer.
 */
export const resolveSpecialInstructionText = (raw: string | null | undefined, values: Record<string, string>): string => {
    if (!raw) return '';

    return raw
        .replace(/@\[([^\]]+)\]/g, (_match, variableId) => values[variableId] || '')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/[ \t]{2,}/g, ' ')
        .trim();
};

export const getModifiedCategories = (
    currentValues: Record<string, any>,
    selectedQuote: Record<string, any>
): {
    isAddressChanged: boolean;
    isDimensionChanged: boolean;
    isAdditionalServiceChanged: boolean;
    checkAdditionalServiceChanged: (serviceType: 'PICKUP' | 'DELIVERY' | 'OTHER' | false, serviceList: any[]) => boolean;
} => {
    if (!selectedQuote)
        return {
            isAddressChanged: false,
            isDimensionChanged: false,
            isAdditionalServiceChanged: false,
            checkAdditionalServiceChanged: () => false,
        };

    const addressFields = ['fullAddress', 'address1', 'city', 'state', 'country', 'postal', 'latitude', 'longitude', 'stateCode', 'countryCode'];

    const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

    const isAddressChanged = (() => {
        const currentShipperAddress = currentValues?.shipper?.shipperAddress || {};
        const selectedShipperAddress = selectedQuote?.quote || {};
        const currentConsigneeAddress = currentValues?.consignee?.consigneeAddress || {};
        const selectedConsigneeAddress = selectedQuote?.quote || {};

        const isShipperChanged = addressFields.some((field) => currentShipperAddress[field] !== selectedShipperAddress[`shipper${capitalizeFirstLetter(field)}`]);

        const isConsigneeChanged = addressFields.some((field) => currentConsigneeAddress[field] !== selectedConsigneeAddress[`consignee${capitalizeFirstLetter(field)}`]);

        return isShipperChanged || isConsigneeChanged;
    })();

    const isDimensionChanged = (() => {
        const currentDimensions = currentValues.order_dimensions || [];
        const selectedDimensions = selectedQuote?.dimensions || [];

        if (currentDimensions.length !== selectedDimensions.length) return true;

        return currentDimensions.some((currDim: any, index: number) => {
            const selDim = selectedDimensions[index] || {};

            return currDim.length !== selDim.length || currDim.width !== selDim.width || currDim.height !== selDim.height || currDim.weight !== selDim.weight;
        });
    })();

    const checkAdditionalServiceChanged = (serviceType: 'PICKUP' | 'DELIVERY' | 'OTHER' | false, serviceList: any[]) => {
        const currentServices = currentValues?.additionalServices || {};
        const selectedServices = selectedQuote?.additionalServices || {};

        let currentServiceIds = Array.isArray(currentServices?.serviceId) ? currentServices.serviceId.sort() : [];
        let selectedServiceIds = Array.isArray(selectedServices?.serviceId) ? selectedServices.serviceId.sort() : [];

        if (serviceType) {
            const servicesListToCheck = serviceList.filter((e) => e.type === serviceType).map((e) => e.id);

            currentServiceIds = currentServiceIds.filter((csi: any) => servicesListToCheck.includes(csi));

            selectedServiceIds = selectedServiceIds.filter((ssi: any) => servicesListToCheck.includes(ssi));

            currentServiceIds = currentServiceIds.sort();
            selectedServiceIds = selectedServiceIds.sort();
        }

        const hasServiceIdChanged = currentServiceIds.length !== selectedServiceIds.length || currentServiceIds.some((id: any, index: number) => id !== selectedServiceIds[index]);

        return hasServiceIdChanged;
    };

    const isAdditionalServiceChanged = checkAdditionalServiceChanged(false, []);

    return {
        isAddressChanged,
        isDimensionChanged,
        isAdditionalServiceChanged,
        checkAdditionalServiceChanged,
    };
};

export const mapAddress = (type: string, selectedQuote: any) => ({
    fullAddress: selectedQuote?.quote?.[`${type}FullAddress`] || '',
    address1: selectedQuote?.quote?.[`${type}Address1`] || '',
    city: selectedQuote?.quote?.[`${type}City`] || '',
    state: selectedQuote?.quote?.[`${type}State`] || '',
    country: selectedQuote?.quote?.[`${type}Country`] || '',
    postal: selectedQuote?.quote?.[`${type}Postal`] || '',
    latitude: selectedQuote?.quote?.[`${type}Latitude`] || '',
    longitude: selectedQuote?.quote?.[`${type}Longitude`] || '',
    stateCode: selectedQuote?.quote?.[`${type}StateCode`] || '',
    countryCode: selectedQuote?.quote?.[`${type}CountryCode`] || '',
    streetNumber: selectedQuote?.quote?.[`${type}StreetNumber`] || '',
});

export const mapShipperConsignee = (type: string, selectedQuote: any) => ({
    id: selectedQuote?.quote?.[`${type}Id`] || null,
    companyName: selectedQuote?.quote?.[`${type}CompanyName`] || '',
    contactId: selectedQuote?.quote?.[`${type}ContactId`] || null,
    contactName: selectedQuote?.quote?.[`${type}ContactName`] || '',
    phoneNumber: selectedQuote?.quote?.[`${type}ContactNumber`] || '',
    email: selectedQuote?.quote?.[`${type}Email`] || '',
    [type === 'shipper' ? 'shipperAddress' : 'consigneeAddress']: mapAddress(type, selectedQuote),
});

export const reverseMapAddress = (type: string, address: any) => ({
    [`${type}FullAddress`]: address.fullAddress || '',
    [`${type}Address1`]: address.address1 || '',
    [`${type}City`]: address.city || '',
    [`${type}State`]: address.state || '',
    [`${type}Country`]: address.country || '',
    [`${type}Postal`]: address.postal || '',
    [`${type}Latitude`]: address.latitude || '',
    [`${type}Longitude`]: address.longitude || '',
    [`${type}StateCode`]: address.stateCode || '',
    [`${type}CountryCode`]: address.countryCode || '',
});

export const reverseMapShipperConsignee = (type: string, shipperConsignee: any) => ({
    [`${type}Id`]: shipperConsignee.id || null,
    [`${type}CompanyName`]: shipperConsignee.companyName || '',
    [`${type}ContactId`]: shipperConsignee.contactId || null,
    [`${type}ContactName`]: shipperConsignee.contactName || '',
    [`${type}ContactNumber`]: shipperConsignee.phoneNumber || '',
    [`${type}Email`]: shipperConsignee.email || '',
    ...reverseMapAddress(type, shipperConsignee[`${type}Address`]),
});

export const getEmailMessageForAppointMent = (actionType: any, currentLegData: any) => {
    switch (actionType) {
        case 'carrier-pickup-appointment':
            return `I hope this message finds you well. We would like to arrange a carrier appointment for pick-up of your order: #${currentLegData?.legId}. Please let us know when you are available.`;
        case 'carrier-delivery-appointment':
            return `I hope this message finds you well. We would like to arrange a carrier appointment for delivery of your order: #${currentLegData?.legId}. Please let us know when you are available.`;
        case 'shipper-appointment':
            return `This is to inform you that your shipment with order #${currentLegData?.legId} is scheduled for pick-up. Kindly confirm the available time for pick-up.`;
        case 'delivery-appointment':
            return `We would like to confirm the delivery details for your order #${currentLegData?.legId}. Please provide us with the suitable time for the delivery.`;
        default:
            return `I hope this message finds you well. Please provide us with the required details for order #${currentLegData?.legId}.`;
    }
};

export const createDateTimeValidation = (dateField: string, timeField: string) => ({
    [dateField]: Yup.date()
        .nullable()
        .test(`${dateField}-required-if-time`, 'Appointment Date is required if time is provided', function (value) {
            const { [timeField]: time } = this.parent;

            return !time || !!value;
        }),

    [timeField]: Yup.string()
        .nullable()
        .test(`${timeField}-required-if-date`, 'Appointment Time is required if date is provided', function (value) {
            const { [dateField]: date } = this.parent;

            return !date || !!value;
        })
        .test('valid-time', 'Invalid time', function (value) {
            const { [dateField]: date } = this.parent;

            if (!date || !value || value === '') return true;

            const [hours, minutes] = value.split(':').map(Number);

            return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
        })
        .test('not-past-time', 'Appointment time cannot be in the past', function (value) {
            const { [dateField]: date } = this.parent;

            if (!date || !value) return true;

            const selectedDateTime = moment.tz(`${moment.utc(date).local().format('YYYY-MM-DD')} ${value}`, 'YYYY-MM-DD HH:mm', moment.tz.guess());

            return selectedDateTime.isSameOrAfter(moment().local());
        }),
});

export const setAppointmentValues = (order: any) => {
    const getDateTimeParts = (datetime: any) => (datetime ? datetime.split(' ') : [undefined, undefined]);

    const convertToLocalDate = (datetime: any) => (datetime ? moment.utc(datetime).local().toDate() : undefined);

    const convertToLocalTime = (datetime: any) => (datetime ? moment.utc(datetime).local().format('HH:mm') : undefined);

    const [shipperAppointmentStartDate] = getDateTimeParts(order?.shipperAppointmentStartDateTime);
    const [shipperAppointmentEndDate] = getDateTimeParts(order?.shipperAppointmentEndDateTime);
    const [carrierPickupAppointmentStartDate] = getDateTimeParts(order?.carrierPickupAppointmentStartDateTime);
    const [carrierPickupAppointmentEndDate] = getDateTimeParts(order?.carrierPickupAppointmentEndDateTime);
    const [deliveryAppointmentStartDate] = getDateTimeParts(order?.deliveryAppointmentStartDateTime);
    const [deliveryAppointmentEndDate] = getDateTimeParts(order?.deliveryAppointmentEndDateTime);
    const [carrierDeliveryAppointmentStartDate] = getDateTimeParts(order?.carrierDeliveryAppointmentStartDateTime);
    const [carrierDeliveryAppointmentEndDate] = getDateTimeParts(order?.carrierDeliveryAppointmentEndDateTime);

    return {
        shipperAppointmentStartDate: convertToLocalDate(shipperAppointmentStartDate),
        shipperAppointmentStartTime: convertToLocalTime(order?.shipperAppointmentStartDateTime),
        shipperAppointmentEndDate: convertToLocalDate(shipperAppointmentEndDate),
        shipperAppointmentEndTime: convertToLocalTime(order?.shipperAppointmentEndDateTime),

        carrierPickupAppointmentStartDate: convertToLocalDate(carrierPickupAppointmentStartDate),
        carrierPickupAppointmentStartTime: convertToLocalTime(order?.carrierPickupAppointmentStartDateTime),

        carrierPickupAppointmentEndDate: convertToLocalDate(carrierPickupAppointmentEndDate),
        carrierPickupAppointmentEndTime: convertToLocalTime(order?.carrierPickupAppointmentEndDateTime),

        deliveryAppointmentStartDate: convertToLocalDate(deliveryAppointmentStartDate),

        deliveryAppointmentStartTime: convertToLocalTime(order?.deliveryAppointmentStartDateTime),

        deliveryAppointmentEndDate: convertToLocalDate(deliveryAppointmentEndDate),

        deliveryAppointmentEndTime: convertToLocalTime(order?.deliveryAppointmentEndDateTime),

        carrierDeliveryAppointmentStartDate: convertToLocalDate(carrierDeliveryAppointmentStartDate),
        carrierDeliveryAppointmentStartTime: convertToLocalTime(order?.carrierDeliveryAppointmentStartDateTime),

        carrierDeliveryAppointmentEndDate: convertToLocalDate(carrierDeliveryAppointmentEndDate),

        carrierDeliveryAppointmentEndTime: convertToLocalTime(order?.carrierDeliveryAppointmentEndDateTime),
    };
};

export const processOrderData = (order: any) => {
    let salesPrice: any = order.salesPrice;
    let carrierPrice: any = [];
    let documents: any = [];
    let claim: any = [];
    let dispute: any = [];

    if (order?.carrierPrice && order?.carrierPrice?.length > 0) {
        carrierPrice = order.carrierPrice.map((carrier: any) => {
            let additionalCharge = carrier.additional_charge ? JSON.parse(carrier.additional_charge) : [];

            if (additionalCharge?.length > 0) {
                additionalCharge = additionalCharge?.map((charge: any) => ({
                    ...charge,
                    isOtherCharge: charge?.isOtherCharge,
                    otherCharge: charge?.isOtherCharge ? charge.charge : null,
                    charge: charge?.isOtherCharge ? 'other' : charge?.charge,
                }));
            }

            const carrierData = carrier.carrier ? JSON.parse(carrier.carrier) : [];

            return {
                ...carrier,
                additional_charge: additionalCharge,
                carrier: carrierData,
            };
        });
    }

    if (order?.documents) {
        documents = Array.isArray(order?.documents) ? order?.documents : JSON.parse(order?.documents);
    }

    if (order?.claim) {
        claim = Array.isArray(order?.claim) ? order?.claim : JSON.parse(order?.claim);
    }

    if (order?.dispute) {
        dispute = Array.isArray(order?.dispute) ? order?.dispute : JSON.parse(order?.dispute);
    }

    return {
        shipper: {
            companyName: order?.shipperCompanyName || '',
            id: order?.shipperId || null,
            contactId: order?.shipperContactId || null,
            contactName: order?.shipperContactName || '',
            phoneNumber: order?.shipperContactNumber || '',
            email: order?.shipperEmail || '',
            shipperAddress: {
                fullAddress: order?.shipperFullAddress || '',
                address1: order?.shipperAddress1 || '',
                city: order?.shipperCity || '',
                state: order?.shipperState || '',
                country: order?.shipperCountry || '',
                postal: order?.shipperPostal || '',
                latitude: order?.shipperLatitude || '',
                longitude: order?.shipperLongitude || '',
                stateCode: order?.shipperStateCode || '',
                countryCode: order?.shipperCountryCode || '',
                streetNumber: order?.shipperStreetNumber || '',
            },
        },
        consignee: {
            id: order?.consigneeId || null,
            contactId: order?.consigneeContactId || null,
            companyName: order?.consigneeCompanyName || '',
            contactName: order?.consigneeContactName || '',
            phoneNumber: order?.consigneeContactNumber || '',
            email: order?.consigneeEmail || '',
            consigneeAddress: {
                fullAddress: order?.consigneeFullAddress || '',
                address1: order?.consigneeAddress1 || '',
                city: order?.consigneeCity || '',
                state: order?.consigneeState || '',
                country: order?.consigneeCountry || '',
                postal: order?.consigneePostal || '',
                latitude: order?.consigneeLatitude || '',
                longitude: order?.consigneeLongitude || '',
                stateCode: order?.consigneeStateCode || '',
                countryCode: order?.consigneeCountryCode || '',
                streetNumber: order?.consigneeStreetNumber || '',
            },
        },
        orderType: order?.orderType || 'ltl',
        equipmentType: order?.equipmentType || 'V',
        equipmentTypeFullName: order?.equipmentTypeFullName || 'Dry Van',
        poNumber: order?.poNumber || '',
        refNumber: order?.refNumber || '',
        linearFootage: order?.linearFootage || undefined,
        totalWeight: order?.totalWeight || undefined,
        declaredValue: order?.declaredValue || undefined,
        specialNotes: order?.specialNotes || '',
        declaredValueUnit: order?.declaredValueUnit || 'CAD',
        order_dimensions: order?.order_dimensions?.length > 0 ? order?.order_dimensions : [dimensionObj],
        additionalServices: order?.additionalServices || null,
        salesPrice,
        carrierPrice,

        carrierPickupAppointmentStartDateTime: order?.carrierPickupAppointmentStartDateTime,
        carrierPickupAppointmentEndDateTime: order?.carrierPickupAppointmentEndDateTime,
        carrierPickupAppointmentStatus: order?.carrierPickupAppointmentStatus,
        carrierPickupAppointmentHasWindow: order?.carrierPickupAppointmentHasWindow,
        carrierPickupAppointmentCreatedById: order?.carrierPickupAppointmentCreatedById,
        carrierPickupAppointmentCreatedBy: order?.carrierPickupAppointmentCreatedBy,
        carrierPickupAppointmentCreatedAt: order?.carrierPickupAppointmentCreatedAt,

        carrierDeliveryAppointmentStartDateTime: order?.carrierDeliveryAppointmentStartDateTime,
        carrierDeliveryAppointmentEndDateTime: order?.carrierDeliveryAppointmentEndDateTime,
        carrierDeliveryAppointmentStatus: order?.carrierDeliveryAppointmentStatus,
        carrierDeliveryAppointmentHasWindow: order?.carrierDeliveryAppointmentHasWindow,
        carrierDeliveryAppointmentCreatedById: order?.carrierDeliveryAppointmentCreatedById,
        carrierDeliveryAppointmentCreatedBy: order?.carrierDeliveryAppointmentCreatedBy,
        carrierDeliveryAppointmentCreatedAt: order?.carrierDeliveryAppointmentCreatedAt,

        shipperAppointmentStartDateTime: order?.shipperAppointmentStartDateTime,
        shipperAppointmentEndDateTime: order?.shipperAppointmentEndDateTime,
        shipperAppointmentStatus: order?.shipperAppointmentStatus,
        shipperAppointmentHasWindow: order?.shipperAppointmentHasWindow,
        shipperAppointmentCreatedById: order?.shipperAppointmentCreatedById,
        shipperAppointmentCreatedBy: order?.shipperAppointmentCreatedBy,
        shipperAppointmentCreatedAt: order?.shipperAppointmentCreatedAt,

        deliveryAppointmentStartDateTime: order?.deliveryAppointmentStartDateTime,
        deliveryAppointmentEndDateTime: order?.deliveryAppointmentEndDateTime,
        deliveryAppointmentStatus: order?.deliveryAppointmentStatus,
        deliveryAppointmentHasWindow: order?.deliveryAppointmentHasWindow,
        deliveryAppointmentCreatedById: order?.deliveryAppointmentCreatedById,
        deliveryAppointmentCreatedBy: order?.deliveryAppointmentCreatedBy,
        deliveryAppointmentCreatedAt: order?.deliveryAppointmentCreatedAt,

        quoteId: order?.quoteId,
        activeCarrierId: order?.activeCarrierId,
        id: order?.id ?? 0,
        orderId: order?.orderId ?? null,
        legId: order?.legId ?? null,
        orderLegId: order?.orderLegId ?? null,
        specialInstructions: order?.specialInstructions || '',
        orderStatus: order?.orderStatus,
        customerMasterId: order?.customerMasterId,
        cadRate: order?.cadRate,
        usdRate: order?.usdRate,
        customerName: order?.customerName,
        customerEmail: order?.customerEmail,
        customerImage: order?.customerImage,
        customerImageUrl: order?.customerImageUrl,
        claimId: order?.claimId,
        disputeId: order?.disputeId,
        createdAt: order?.createdAt,
        documents: documents,
        claim: claim[0] ? Object.assign({}, claim[0]) : {},
        dispute: dispute[0] ? Object.assign({}, dispute[0]) : {},
        salesRepEmail: order?.salesRepEmail,
        salesRepFirstName: order?.salesRepFirstName,
        salesRepId: order?.salesRepId,
        salesRepLastName: order?.salesRepLastName,
        legPosition: order?.legPosition,
        isLastLeg: order?.isLastLeg,
        expectedPickupTime: order?.expectedPickupTime,
        expectedEstimatedDeliveryTime: order?.expectedEstimatedDeliveryTime,
        weightMeasurement: order?.order_dimensions?.length > 0 ? order?.order_dimensions?.[0]?.weightMeasurement : MEASUREMENT.WEIGHT1.value,
        lengthMeasurement: order?.order_dimensions?.length > 0 ? order?.order_dimensions?.[0]?.lengthMeasurement : MEASUREMENT.LENGTH1.value,
        isRateConfirmationSent: order?.isRateConfirmationSent,
        rateConfirmationSentAt: order?.rateConfirmationSentAt,
        isPodRequestSent: order?.isPodRequestSent,
        podRequestSentAt: order?.podRequestSentAt,
        isPodRejected: order?.isPodRejected,
        isPodUploaded: order?.isPodUploaded,
        isRateConfirmationAcceptedByCarrier: order?.isRateConfirmationAcceptedByCarrier,
        rateConfirmationAcceptedByCarrierAt: order?.rateConfirmationAcceptedByCarrierAt,
        brokerId: order?.brokerId,
        brokerName: order?.brokerName,
        brokerEmail: order?.brokerEmail,
        roserocketPublicId: order?.roserocketPublicId,
        rossRocketId: order?.rossRocketId,
        quoteRossRocketId: order?.quoteRossRocketId,
        actualPickupTime: order?.actualPickupTime,
        actualEstimatedDeliveryTime: order?.actualEstimatedDeliveryTime,
        isCustomerInvoicetSent: order?.isCustomerInvoicetSent,
        customerInvoiceSentAt: order?.customerInvoiceSentAt,
        pickupNumber: order?.pickupNumber,
        deliveryNumber: order?.deliveryNumber,
        commodity: order?.commodity,
        lastLocation: order?.lastLocation,
        pendingSpecialInstructions: order?.pendingSpecialInstructions || null,
    };
};

export const getNextAction = (
    status: any,
    data: {
        checkCarrierAssigned: boolean;
        currentLocation: any;
        documents: any[];
        activeLegData: any;
        activeLegCarrier: any[];
        appearance: any;
    }
) => {
    const { checkCarrierAssigned, currentLocation, documents, activeLegData, activeLegCarrier, appearance } = data;

    // Get priority pickup and delivery dates using centralized logic
    const priorityPickupDateTime = getPriorityPickupDateTime(activeLegData, appearance);
    const priorityDeliveryDateTime = getPriorityDeliveryDateTime(activeLegData, appearance);

    const shipperAppointmentStartDateTime = activeLegData?.shipperAppointmentStartDateTime;
    const carrierPickupAppointmentStartDateTime = activeLegData?.carrierPickupAppointmentStartDateTime;
    const shipperAppointmentEndDateTime = activeLegData?.shipperAppointmentEndDateTime;
    const shipperAppointmentHasWindow = activeLegData?.shipperAppointmentHasWindow;
    const isLastLeg = activeLegData?.isLastLeg;
    const carrier = activeLegData?.carrierPrice?.length > 0 ? activeLegData?.carrierPrice?.[0]?.carrier : [];

    const isPassedAnHour = moment().utc().diff(moment.utc(activeLegData?.rateConfirmationSentAt), 'hours') >= 1;

    const podCollectionLinkSentAt = activeLegData?.podRequestSentAt ? moment.utc(activeLegData?.podRequestSentAt).tz(appearance?.timezone).format('Do MMM YYYY [at] hh:mm A') : null;

    const customerName = activeLegData?.customerName;
    const customerInvoiceSentAt = activeLegData?.customerInvoiceSentAt ? moment.utc(activeLegData?.customerInvoiceSentAt).tz(appearance?.timezone).format('Do MMM YYYY [at] hh:mm A') : null;

    switch (status) {
        case 'booked':
            if (!checkCarrierAssigned) {
                return {
                    title: 'Order Created - Assign a Carrier',
                    description: 'To dispatch this order, please assign a carrier to generate the rate confirmation.',
                };
            }

            return {
                title: 'Order Booked',
                description: 'Your order has been successfully booked.',
            };

        case 'dispatched': {
            if ((shipperAppointmentStartDateTime || carrierPickupAppointmentStartDateTime) && !activeLegData?.isRateConfirmationSent) {
                const appointmentTime = shipperAppointmentHasWindow
                    ? `${moment.utc(priorityPickupDateTime).tz(appearance?.timezone).format('hh:mm A')} - ${moment.utc(shipperAppointmentEndDateTime).tz(appearance?.timezone).format('hh:mm A')}`
                    : moment.utc(priorityPickupDateTime).tz(appearance?.timezone).format('hh:mm A');

                return {
                    title: `Pickup Appointment Scheduled ${getFormattedDateWithTime(priorityPickupDateTime, 'dddd, MMM Do', false, true, appearance)} at ${appointmentTime}`,
                    description: 'Pick the exact time between carrier and shipper',
                };
            }

            if (!isPassedAnHour && activeLegData?.isRateConfirmationSent && activeLegCarrier?.length > 0) {
                return {
                    title: 'Order Dispatched - Rate Confirmation Sent',
                    description: `Sent to ${activeLegCarrier[0]?.carrier?.name}. on ${getFormattedDateWithTime(
                        activeLegData?.rateConfirmationSentAt,
                        'dddd, MMM Do',
                        true,
                        true,
                        appearance
                    )}. You can schedule the pickup or wait for carrier confirmation.`,
                };
            }

            if (isPassedAnHour && activeLegData?.isRateConfirmationSent) {
                const formattedPickupDateWithTime = getFormattedDateWithTime(priorityPickupDateTime, 'dddd, MMM Do', true, true, appearance);
                const pickingUpFormattedDate = getDateWithSuffixFormat(formattedPickupDateWithTime);
                const isSameWeekPickup = moment.utc(priorityPickupDateTime).tz(appearance?.timezone).isSame(moment().tz(appearance?.timezone), 'week');

                return {
                    title: `Order Dispatched. Picking up ${isSameWeekPickup ? 'this' : 'on'} ${pickingUpFormattedDate}`,
                    description: 'The pickup time has been scheduled. Mark as picked up once the carrier confirms loading.',
                };
            }

            return {
                title: 'Order Dispatched - Send Carrier Rate Confirmation',
                description: `A carrier is assigned, but the Rate Confirmation hasn't been sent. Please confirm rates before pickup.`,
            };
        }

        case 'in-transit': {
            const customDocuments = documents?.filter((doc: any) => doc?.documentType === ORDER_DOCUMENT_TYPE.CUSTOMS_DOCUMENT);

            if (currentLocation && activeLegData?.consignee?.consigneeAddress?.countryCode !== activeLegData?.shipper?.shipperAddress?.countryCode && !(customDocuments?.length > 0)) {
                const locationUpdatedAt = moment.utc(currentLocation.createdAt).fromNow();

                return {
                    title: 'Shipment In Transit. Customs Documents Required',
                    description: `Last location update was ${locationUpdatedAt} near ${currentLocation?.state}. Please upload customs documents to avoid delays.`,
                };
            }

            if (currentLocation) {
                const city = currentLocation?.city;
                const stateCode = currentLocation?.stateCode;

                const locationUpdatedAt = moment.utc(currentLocation.createdAt).fromNow();

                const estimatedTimeToDelivery = moment.utc(priorityDeliveryDateTime).from(moment.utc());

                return {
                    title: `Shipment In Transit. Last reported near ${city}, ${stateCode}`,
                    description: `Last known location updated ${locationUpdatedAt}. Estimated time to delivery is ${estimatedTimeToDelivery}.`,
                };
            }

            return {
                title: 'Shipment In Transit',
                description: 'The location status has not been updated.',
            };
        }

        case 'delivered': {
            const podDocument = documents?.find((doc: any) => doc.documentType === 'POD');

            const isDocuementApproved = podDocument?.length > 0 ? podDocument?.isApproved : false;

            const isPODUploaded = activeLegData?.isPodUploaded;
            const isPodRejected = activeLegData?.isPodRejected;

            if (isPodRejected) {
                return {
                    title: 'Documents Rejected - Carrier Action Required.',
                    description: `We have notified ${carrier?.name} to reupload corrected documents. The order remains on hold until new documents are submitted.`,
                };
            }

            if (isPODUploaded) {
                return {
                    title: 'POD & Invoice Uploaded. Please review documents.',
                    description: `${carrier?.name} uploaded the documents with no additional charges. Please review and approve.`,
                };
            }

            if (podDocument?.length > 0) {
                return {
                    title: 'Shipment Delivered. Please Request And Review POD',
                    description: 'Waiting for the carrier to provide POD and invoice',
                };
            }

            if (isDocuementApproved) {
                return {
                    title: 'Documents Approved. Please Invoice Your Customer.',
                    description: 'All carrier documents and charges are settled. Proceed with invoicing your customer.',
                };
            }

            if (podDocument) {
                return {
                    title: 'Order Delivered. Awaiting Carrier POD & Invoice.',
                    description: `POD collection link was sent to ${carrier?.name} on ${podCollectionLinkSentAt}. We'll notify you once the carrier uploads their documents.`,
                };
            }

            if (activeLegData?.isPodRequestSent) {
                return {
                    title: 'Order Delivered. Awaiting Carrier POD & Invoice.',
                    description: `POD collection link was sent to ${carrier?.name} on ${podCollectionLinkSentAt}. We'll notify you once the carrier uploads their documents.`,
                };
            }

            return {
                title: 'Order Delivered. Collect POD & Invoice.',
                description: 'Send a secure link to the carrier so they can upload the POD and invoice for payment processing',
            };

            return {
                title: isLastLeg ? 'Customer Invoiced, Awaiting Payment' : 'Preparing Next Delivery',
                description: isLastLeg ? 'Payment will be processed via QuickBooks' : 'The order is being prepared for the next delivery leg',
                isOrderInReview: false,
                isInvoiced: true,
            };
        }

        case 'dispute': {
            return {
                title: 'Order on Hold. Dispute In Progress.',
                description: `The carrier's invoice is being disputed. Resolve the dispute to proceed with final billing.`,
                isOrderInReview: true,
            };
        }

        case 'invoice-created':
            return {
                title: 'Documents Approved. Please Invoice Your Customer.',
                description: 'All carrier documents and charges are settled. Proceed with invoicing your customer.',
                isInvoiced: true,
            };

        case 'invoice-sent':
            return {
                title: 'Customer Invoice Sent. Awaiting Payment.',
                description: `Invoice sent to ${customerName} on ${customerInvoiceSentAt}. Awaiting payment confirmation.`,
                isInvoiced: true,
            };

        case 'invoice-paid':
            return {
                title: 'Customer Invoice Paid. Order Completed.',
                description: 'Payment received from Olivia Rhye on 27th Feb 2025 at 03:30 PM. This order is now completed.',
            };

        case 'claim':
            return {
                title: 'Order On Hold. Claim in Progress',
                description: 'A claim has been filed for this shipment. Please review the claim details and determine next steps',
                isOrderInReview: true,
            };

        case 'completed':
            return {
                title: 'Order Completed. Customer Payment Received.',
                description: 'All final billing and payment steps are complete. This order is now completed.',
                isOrderCompleted: true,
            };

        default:
            return {
                title: 'Order Created - Assign a Carrier',
                description: 'To dispatch this order, please assign a carrier to generate the rate confirmation.',
            };
    }
};

export const getHourAndMinuteDifference = (givenDate: string, t: any) => {
    const dateUtc = moment.utc(givenDate, 'YYYY-MM-DD HH:mm:ss');
    const nowUtc = moment.utc();
    const diffInMinutes = dateUtc.diff(nowUtc, 'minutes');
    const diffInHours = dateUtc.diff(nowUtc, 'hours');

    if (diffInMinutes <= 0) {
        return null;
    }

    if (diffInHours >= 1 && diffInHours < 24) {
        const hours = diffInHours;
        const minutes = diffInMinutes % 60;

        return `${t('Common.in')} ${hours} ${hours !== 1 ? t('Radar.hours') : t('Radar.hour')}${minutes > 0 ? `, ${minutes} ${minutes !== 1 ? t('Radar.minutes') : t('Radar.minute')}` : ''}`;
    } else if (diffInHours < 1) {
        return `${t('Common.in')} ${diffInMinutes} ${diffInMinutes !== 1 ? t('Radar.minutes') : t('Radar.minute')}`;
    } else {
        return null;
    }
};

export const formatTimezone = (timezone: string, showRegion: boolean = true): string => {
    if (!timezone) return '';
    const formatted = timezone.replace(/_/g, ' ');

    if (!showRegion) {
        const parts = formatted.split('/');

        return parts[parts.length - 1];
    }

    return formatted;
};

// ── Customer-facing leg instructions (pickup / delivery) ─────────────────────

/**
 * Statuses at or past delivery. The customer can no longer influence how the freight is
 * handled once it has been delivered, so every self-serve action on the order detail page
 * is withdrawn from here on. `delivered` alone is not enough — an order moves on to the
 * invoice/completed statuses, and checking only `delivered` let the actions reappear.
 */
// 'claim' and 'dispute' are WAL-internal post-delivery states; the customer sees them as delivered
// (Lucas, 2026-09-05: nothing about claims or disputes ever shows on the customer's card).
const POST_DELIVERY_ORDER_STATUSES: string[] = [ORDER_STATUS.DELIVERED, 'claim', 'dispute', ORDER_STATUS.INVOICE_CREATED, ORDER_STATUS.INVOICE_SENT, ORDER_STATUS.INVOICE_PAID, ORDER_STATUS.COMPLETED];

export const isPostDeliveryOrderStatus = (status?: string | null): boolean => !!status && POST_DELIVERY_ORDER_STATUSES.includes(status);

/**
 * Which instruction the customer may edit for a leg, based on how far along it is:
 *  - before in-transit (booked / dispatched / …) → pickup instructions, the freight has
 *    not been collected yet so pickup guidance is still actionable
 *  - in-transit → delivery instructions, pickup already happened
 *  - delivered and beyond → null, nothing left to instruct
 *
 * Both map to real order_legs columns (pickupInstruction / deliveryInstruction) that the
 * WAL order detail page renders in its Order Instructions card.
 */
export const getLegInstructionNoteType = (orderStatus?: string | null): 'pickup' | 'delivery' | null => {
    if (!orderStatus || isPostDeliveryOrderStatus(orderStatus)) return null;

    return orderStatus === ORDER_STATUS.IN_TRANSIT ? 'delivery' : 'pickup';
};

export const getOrderStatusBadgeType = (status: any): BadgeType => {
    if (status === 'draft' || status === 'cancelled' || status === 'dispatched') {
        return 'pink';
    } else if (status === 'booked' || status === 'delivered') {
        return 'lightblue';
    } else if (status === 'in-transit' || status === 'booking_failed') {
        return 'error';
    } else if (status === 'completed') {
        return 'gray';
    }

    return 'red';
};

// ── Section 4.2 — Status Title Parts (customer portal) ───────────────────────

export interface TitleParts {
    primary: string;
    secondary?: string;
    secondaryTitleClassName?: string;
}

export interface DynamicFunctionConfig {
    placeholder: string;
    function: (...args: any[]) => any;
    args: any[];
}

export interface StatusTitleResult {
    title: TitleParts;
    description: string;
    functions?: DynamicFunctionConfig[];
}

/**
 * Returns a relative time display for a pickup datetime in the given timezone.
 * Supports pickup windows: "(Between 9:00 AM-3:00 PM)" format when isPickupWindow + pickupStartDateTime provided.
 * Mirrors the reference getDynamicPickupTimeDisplay from wal-frontend-v2 exactly.
 */
export const getDynamicPickupTimeDisplay = (priorityPickupDateTime: string | null | undefined, timezone: string, pickupStartDateTime?: string | null, isPickupWindow?: boolean): string => {
    if (!priorityPickupDateTime || !timezone) return '';

    const pickupMoment = moment.tz(priorityPickupDateTime, timezone);
    const now = moment.tz(timezone);
    const tz = pickupMoment.format('z');
    const diffInHours = pickupMoment.diff(now, 'hours');

    const getWindowDisplay = (): string | null => {
        if (isPickupWindow && pickupStartDateTime) {
            const startMoment = moment.tz(pickupStartDateTime, timezone);

            return `(Between ${startMoment.format('h:mm A')}-${pickupMoment.format('h:mm A')} ${tz})`;
        }

        return null;
    };

    if (pickupMoment.isBefore(now)) {
        const diffInDays = now.diff(pickupMoment, 'days');
        const windowDisplay = getWindowDisplay();

        if (pickupMoment.isSame(now, 'day')) {
            return windowDisplay ? `Today ${windowDisplay}` : `Today at ${pickupMoment.format('h:mm A')} (${tz})`;
        } else if (diffInDays === 1) {
            return windowDisplay ? `Yesterday ${windowDisplay}` : `Yesterday at ${pickupMoment.format('h:mm A')} (${tz})`;
        } else if (diffInDays < 7) {
            return windowDisplay ? `${pickupMoment.format('dddd')} ${windowDisplay}` : `${pickupMoment.format('dddd')} at ${pickupMoment.format('h:mm A')} (${tz})`;
        } else {
            return windowDisplay ? `${pickupMoment.format('MMM Do')} ${windowDisplay}` : `${pickupMoment.format('MMM Do')} at ${pickupMoment.format('h:mm A')} (${tz})`;
        }
    }

    const windowDisplay = getWindowDisplay();

    if (pickupMoment.isSame(now, 'day')) {
        return windowDisplay ? `Today ${windowDisplay}` : `Today at ${pickupMoment.format('h:mm A')} (${tz})`;
    }

    if (diffInHours < 48 && pickupMoment.diff(now, 'days') === 1) {
        return windowDisplay ? `Tomorrow ${windowDisplay}` : `Tomorrow at ${pickupMoment.format('h:mm A')} (${tz})`;
    }

    if (diffInHours < 72) {
        return windowDisplay ? `${pickupMoment.format('dddd')} ${windowDisplay}` : `${pickupMoment.format('dddd')} at ${pickupMoment.format('h:mm A')} (${tz})`;
    }

    // 3+ days out: bare "in N days" (fromNow) was too vague on its own. Under 5 days show
    // the countdown with the concrete date in brackets — "in 4 days (Aug 18th, 2026)";
    // 5 days or more the countdown itself stops being useful, so lead with the date.
    const dateDisplay = pickupMoment.format('MMM Do, YYYY');
    const daysUntil = pickupMoment.clone().startOf('day').diff(now.clone().startOf('day'), 'days');

    if (daysUntil < 5) {
        return `in ${daysUntil} days (${dateDisplay})`;
    }

    return `on ${dateDisplay}`;
};

/**
 * Returns true if the pickup datetime has already passed.
 * Mirrors isPickupTimeInPast from wal-frontend-v2.
 */
export const isPickupTimeInPast = (priorityPickupDateTime: string | null, timezone: string | null): boolean => {
    if (!priorityPickupDateTime || !timezone) return false;

    // Times are already in the location's timezone (local) — parse directly without UTC conversion
    const pickupMoment = moment.tz(priorityPickupDateTime, timezone);
    const now = moment.tz(timezone);

    return pickupMoment.isBefore(now);
};

/**
 * Returns a relative time display for a delivery datetime in the given timezone.
 * Uses "by" preposition (delivery convention) vs pickup's "at".
 * Mirrors PRD delivery time format table exactly.
 */
export const getDynamicDeliveryTimeDisplay = (deliveryDateTime: string | null | undefined, timezone: string, deliveryStartDateTime?: string | null, isDeliveryWindow?: boolean): string => {
    if (!deliveryDateTime || !timezone) return '';

    const deliveryMoment = moment.tz(deliveryDateTime, timezone);
    const now = moment.tz(timezone);
    const tz = deliveryMoment.format('z');

    const getWindowDisplay = (): string | null => {
        if (isDeliveryWindow && deliveryStartDateTime) {
            const startMoment = moment.tz(deliveryStartDateTime, timezone);

            return `(Between ${startMoment.format('h:mm A')}-${deliveryMoment.format('h:mm A')} ${tz})`;
        }

        return null;
    };

    if (deliveryMoment.isBefore(now)) {
        const diffInDays = now.diff(deliveryMoment, 'days');
        const windowDisplay = getWindowDisplay();

        if (deliveryMoment.isSame(now, 'day')) {
            return windowDisplay ? `earlier today ${windowDisplay}` : `earlier today by ${deliveryMoment.format('h:mm A')} (${tz})`;
        } else if (diffInDays === 1) {
            return windowDisplay ? `yesterday ${windowDisplay}` : `yesterday by ${deliveryMoment.format('h:mm A')} (${tz})`;
        } else if (diffInDays < 7) {
            return windowDisplay ? `${deliveryMoment.format('dddd')} ${windowDisplay}` : `${deliveryMoment.format('dddd')} by ${deliveryMoment.format('h:mm A')} (${tz})`;
        } else {
            return windowDisplay ? `${deliveryMoment.format('MMM Do')} ${windowDisplay}` : `${deliveryMoment.format('MMM Do')} by ${deliveryMoment.format('h:mm A')} (${tz})`;
        }
    }

    const diffInHours = deliveryMoment.diff(now, 'hours');
    const windowDisplay = getWindowDisplay();

    if (deliveryMoment.isSame(now, 'day')) {
        return windowDisplay ? `later today ${windowDisplay}` : `later today by ${deliveryMoment.format('h:mm A')} (${tz})`;
    }

    if (diffInHours < 48 && deliveryMoment.diff(now, 'days') === 1) {
        return windowDisplay ? `tomorrow ${windowDisplay}` : `tomorrow by ${deliveryMoment.format('h:mm A')} (${tz})`;
    }

    if (diffInHours < 72) {
        return windowDisplay ? `${deliveryMoment.format('dddd')} ${windowDisplay}` : `${deliveryMoment.format('dddd')} by ${deliveryMoment.format('h:mm A')} (${tz})`;
    }

    return deliveryMoment.fromNow();
};

/**
 * "25 minutes ago" for a UTC timestamp (location pings). Used as a live `{{lastSeenAgo}}`
 * placeholder so the card re-renders the age every second like the other dynamic fields.
 */
export const getRelativeTimeAgo = (dateTime: string | null | undefined): string => (dateTime ? moment.utc(dateTime).fromNow() : '');

/**
 * Returns true if the delivery datetime has already passed.
 */
export const isDeliveryTimeInPast = (deliveryDateTime: string | null, timezone: string | null): boolean => {
    if (!deliveryDateTime || !timezone) return false;

    const deliveryMoment = moment.tz(deliveryDateTime, timezone);
    const now = moment.tz(timezone);

    return deliveryMoment.isBefore(now);
};

/** Leg documents as an array — the API sometimes hands them over as a JSON string. */
const parseDocuments = (documents: any): any[] => {
    if (Array.isArray(documents)) return documents;
    if (typeof documents !== 'string') return [];

    try {
        const parsed = JSON.parse(documents);

        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

/**
 * Returns true when the leg has at least one POD document.
 * `documentType` casing is inconsistent in `order_documents` (rows exist as both 'POD' and 'pod'),
 * so the comparison is case-insensitive. Also tolerates `documents` arriving as a JSON string.
 */
export const hasPodDocument = (documents: any): boolean => parseDocuments(documents).some((doc: any) => String(doc?.documentType ?? '').toLowerCase() === ORDER_DOCUMENT_TYPE.POD.toLowerCase());

/**
 * The Bill of Lading an action item is asking the customer to review: the newest BOL on the order
 * that is still the current version and has not been rejected. Mirrors the BOL_REVIEW rule in the
 * portal backend's getReviewItems.controller.ts, which matches order_documents on
 * `documentType = 'BOL' AND isLatestVersion AND NOT isRejected`, newest first.
 *
 * Order-level documents come first because with "Show as Multi-Leg" off the backend strips every
 * leg BOL in favour of the master one — but a single-leg order has no master BOL (WAL skips
 * generating one), so the leg's own BOL is the fallback. Returns null when the order carries none,
 * in which case the caller simply leaves the customer on the page.
 */
export const findLatestBolDocument = (order: any): any => {
    const orderLevelDocs = parseDocuments(order?.orderDocuments);
    const legDocs = (order?.orderLegs ?? []).flatMap((leg: any) => parseDocuments(leg?.documents));

    const bolDocs = [...orderLevelDocs, ...legDocs].filter(
        (doc: any) =>
            String(doc?.documentType ?? '').toUpperCase() === ORDER_DOCUMENT_TYPE.BOL &&
            // Older payloads omit these flags — absent means "current, not rejected", never excluded.
            doc?.isLatestVersion !== false &&
            doc?.isRejected !== true
    );

    if (!bolDocs.length) return null;

    return bolDocs.sort((a: any, b: any) => moment(b?.createdAt).valueOf() - moment(a?.createdAt).valueOf())[0];
};

/**
 * The PHYSICAL leg that makes a presented leg's delivery. With "Display Order Legs" off the backend
 * merges internal legs into one presented span (mergedLegIds, in route order): the delivery is made
 * by the LAST physical leg of that span. A real physical leg delivers itself.
 */
export const getDeliveringLegId = (leg: any): any => {
    const merged = leg?.mergedLegIds;

    if (Array.isArray(merged) && merged.length > 0) return merged[merged.length - 1];

    return leg?.orderLegId ?? leg?.id ?? null;
};

/**
 * Whether the presented leg's DELIVERY has a proof of delivery. A merged span unions the documents
 * of every physical leg it covers, so a POD filed on an inner (hand-off) leg sits in the same list
 * as one filed on the delivering leg — only the latter means the freight reached the customer.
 * Documents carry the physical leg they were filed on (`orderLegId`); a document without it comes
 * from an older payload and is taken at face value so the card never goes blank on stale data.
 */
export const hasDeliveryPodDocument = (leg: any): boolean => {
    const deliveringLegId = getDeliveringLegId(leg);
    const docs = parseDocuments(leg?.documents).filter((doc: any) => doc?.orderLegId == null || deliveringLegId == null || doc.orderLegId === deliveringLegId);

    return hasPodDocument(docs);
};

const pluralizeUnit = (unit: string, count: number): string => {
    if (count === 1) return unit;
    if (/(s|x|z|ch|sh)$/i.test(unit)) return `${unit}es`;
    if (/[^aeiou]y$/i.test(unit)) return `${unit.slice(0, -1)}ies`;

    return `${unit}s`;
};

/**
 * buildItemsPhrase — "4 pallets" / "2 skids" summary of a set of line items for NBA subtitles.
 *
 * Sums handlingUnitNo across the dims (missing count = 1, matching the Load Details table).
 * The unit word comes from the shared handlingUnitOptions lookup; when the dims mix unit
 * types (or the label can't resolve) it degrades to "N items". Returns null when nothing
 * is resolvable — callers drop the items fragment entirely in that case.
 */
export const buildItemsPhrase = (dims: any[], handlingUnitOptions?: any[]): string | null => {
    if (!Array.isArray(dims) || dims.length === 0) return null;

    const total = dims.reduce((sum: number, dim: any) => sum + (Number(dim?.handlingUnitNo) || 1), 0);
    if (total <= 0) return null;

    const labels = new Set(dims.map((dim: any) => handlingUnitOptions?.find((option: any) => option?.value === Number(dim?.handlingUnit))?.label ?? null));
    const singleLabel = labels.size === 1 ? Array.from(labels)[0] : null;
    const unit = singleLabel ? String(singleLabel).toLowerCase() : 'item';

    return `${total} ${pluralizeUnit(unit, total)}`;
};

/**
 * Per-leg context for the client-approved NBA wording (Aug 2026).
 * The item phrases are pre-resolved by the caller (they need the leg ordering + redux
 * handling-unit labels): itemsPickedUp = items boarding at this leg's start,
 * itemsOnBoard = items riding this leg, itemsDelivered = items ending at this leg's
 * end. null = not resolvable → omitted.
 */
export interface NbaWordingContext {
    itemsPickedUp?: string | null;
    itemsOnBoard?: string | null;
    itemsDelivered?: string | null;
    /** False when the viewed leg is not the order's last stop: a delivered leg then reads as an arrival, not "Delivered". */
    isFinalStop?: boolean;
    /** The stop this leg arrives at, and the stop after it (next leg's destination). */
    arrivalCity?: string | null;
    nextStopCity?: string | null;
}

/**
 * Derives the NBA status title parts (Title + Subtitle + Description) for the CP order detail page.
 * Titles/subtitles follow the client-approved wording matrix (Aug 2026): subtitles carry
 * the live "what" (time · items). Descriptions keep their original PRD wording. Maps
 * internal statuses to 4 customer-facing statuses: Confirmed, In Transit, Delivered,
 * Completed. The "Stop N of M" indicator lives only in the card footer, not the title.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getCpStatusTitleParts = (
    activeLegData: any,
    currentLegData: any,
    _isCarrierAssigned: boolean,
    documents: any[],
    currentLocation?: any,
    nba: NbaWordingContext = {}
): StatusTitleResult => {
    const status = activeLegData?.orderStatus;

    // ── Helper: extract pickup schedule data ─────────────────────────────────────
    const getPickupScheduleData = () => {
        const pickupSchedule = currentLegData?.pickupDeliverySchedule;
        // Schedule timezone, else the shipper address timezone, else the portal's default (the same
        // hardcoded fallback ProgressList uses): a missing timezone must not hide a confirmed date.
        const pickupTimezone = pickupSchedule?.pickupTimezone || currentLegData?.shipper?.shipperAddress?.timezone || 'America/New_York';
        const pickupWindowType = pickupSchedule?.currentPickupWindowType || pickupSchedule?.initialPickupWindowType || 'open';

        // Mirror OrderLegDetailsSection: when windowType is 'open' with no raw times, inject shipper business hours.
        // This ensures the status subtitle matches what the Pickup Time & Scheduling section displays.
        const rawStartTime = pickupSchedule?.currentPickupStartTime || '';
        const rawEndTime = pickupSchedule?.currentPickupEndTime || '';
        const bhStart = currentLegData?.shipper?.businessHoursStartTime || '';
        const bhEnd = currentLegData?.shipper?.businessHoursEndTime || '';
        const shouldInjectBH = pickupWindowType === 'open' && !rawStartTime && !!bhStart;
        const effectiveStartTime = shouldInjectBH ? bhStart : rawStartTime;
        const effectiveEndTime = shouldInjectBH ? bhEnd : rawEndTime;

        // Named window types OR business hours with both start+end constitute a window
        const isPickupWindow = pickupWindowType === 'ready_time' || pickupWindowType === 'delivery_time' || (shouldInjectBH && !!bhEnd);

        // For same-day windows the API sends currentPickupEndDate=null and reuses currentPickupStartDate as the base date
        const pickupEndDate = pickupSchedule?.currentPickupEndDate || pickupSchedule?.currentPickupStartDate;
        const pickupDateTime = isPickupWindow
            ? pickupEndDate && effectiveEndTime
                ? `${pickupEndDate} ${effectiveEndTime}`
                : null
            : pickupSchedule?.currentPickupStartDate && effectiveStartTime
            ? `${pickupSchedule.currentPickupStartDate} ${effectiveStartTime}`
            : null;
        const pickupStartFormatted = isPickupWindow && pickupSchedule?.currentPickupStartDate && effectiveStartTime ? `${pickupSchedule.currentPickupStartDate} ${effectiveStartTime}` : null;

        return { pickupTimezone, isPickupWindow, pickupDateTime, pickupStartFormatted, pickupDate: pickupSchedule?.currentPickupStartDate || null };
    };

    // ── Helper: a finished leg that is NOT the order's last stop ─────────────────
    // Whether the leg sits at delivered, completed or an invoicing state, the customer's order is
    // still moving (multi-leg wording, 2026-09-04): the card reads as an arrival on the way, never
    // "Delivered" / "Completed". Items dropped here are named; otherwise the shipment simply reached
    // this stop and continues. Never the word "leg": with legs hidden the customer only knows stops.
    const buildIntermediateStopReached = (): StatusTitleResult => {
        const here = nba.arrivalCity || 'this stop';
        const next = nba.nextStopCity || 'its next stop';

        if (nba.itemsDelivered) {
            return {
                title: { primary: `Delivered in ${here}`, secondary: `${nba.itemsDelivered} delivered · rest continuing to ${next}` },
                description: `${withItemsVerb(nba.itemsDelivered, 'was', 'were')} delivered in ${here}. The rest continues to ${next}.`,
            };
        }

        return {
            title: { primary: `Arrived in ${here}`, secondary: `Continuing to ${next}` },
            description: `Your shipment reached ${here} and continues to ${next}.`,
        };
    };

    // ── CONFIRMED: booked, dispatched, booking_in_progress ───────────────────────
    if (status === 'booked' || status === 'dispatched' || status === 'booking_in_progress') {
        const { pickupTimezone, isPickupWindow, pickupDateTime, pickupStartFormatted, pickupDate } = getPickupScheduleData();

        // WALHQ nearly always confirms the pickup DATE at booking; the time may follow. With a date and
        // no time (and no business-hours window to fall back on) the card still shows the day and says
        // the time is coming. "Pickup date coming soon" is only for an order with no date at all.
        const isDateOnly = !pickupDateTime && !!pickupDate;

        if (!pickupDateTime && !pickupDate) {
            return {
                title: { primary: 'Order Confirmed', secondary: 'Pickup date coming soon' },
                description: "Your order is confirmed. We'll share the pickup date shortly.",
            };
        }

        const pickupTimeFn: DynamicFunctionConfig = isDateOnly
            ? { placeholder: '{{pickupTime}}', function: getDynamicPickupDateDisplay, args: [pickupDate, pickupTimezone] }
            : { placeholder: '{{pickupTime}}', function: getDynamicPickupTimeDisplay, args: [pickupDateTime, pickupTimezone, pickupStartFormatted, isPickupWindow] };

        const buildConfirmed = (description: string): StatusTitleResult => ({
            title: { primary: 'Order Confirmed', secondary: `Pickup {{pickupTime}}${nba.itemsPickedUp ? ` · ${nba.itemsPickedUp}` : ''}` },
            description,
            functions: [pickupTimeFn],
        });

        // Date-only pickups count as past only once the whole day is over.
        const pickupMoment = isDateOnly ? moment.tz(pickupDate, pickupTimezone).endOf('day') : moment.tz(pickupDateTime, pickupTimezone);
        const now = moment.tz(pickupTimezone);
        const diffInHours = pickupMoment.diff(now, 'hours');
        const readyPhrase = isDateOnly
            ? "We'll confirm the pickup time with you."
            : isPickupWindow
            ? 'Please ensure your shipment is ready within the confirmed window.'
            : 'Please ensure your shipment is ready at the confirmed time.';

        if (pickupMoment.isBefore(now)) {
            return buildConfirmed("We're verifying the pickup status. Your sales representative will update you shortly.");
        }

        if (pickupMoment.isSame(now, 'day')) {
            return buildConfirmed(`Your pickup is scheduled for today. ${readyPhrase}`);
        }

        if (diffInHours < 48 && pickupMoment.diff(now, 'days') === 1) {
            return buildConfirmed(`Your pickup is scheduled for tomorrow. ${readyPhrase}`);
        }

        if (diffInHours < 72) {
            return buildConfirmed(`Your pickup is coming up soon. ${readyPhrase}`);
        }

        return buildConfirmed(
            isDateOnly
                ? "Your order is confirmed and a pickup date has been set. We'll confirm the pickup time with you."
                : 'Your order is confirmed and a pickup has been scheduled. You will be notified of any updates.'
        );
    }

    // ── BOOKING FAILED ───────────────────────────────────────────────────────────
    // Internal retry state — never hint at backend trouble to the customer, and never
    // let the raw enum leak ("Order Booking_failed"). Presented as confirmed with a
    // carrier-search subtitle until the booking is retried.
    if (status === 'booking_failed') {
        return {
            title: { primary: 'Order Confirmed', secondary: 'Pickup date coming soon' },
            description: "Your order is confirmed. We'll share the pickup date shortly.",
        };
    }

    // ── IN TRANSIT ───────────────────────────────────────────────────────────────
    if (status === 'in-transit') {
        const rawLocation = currentLegData?.lastUpdatedLocation || currentLocation || null;
        const locationCreatedAt = rawLocation?.createdAt ?? null;
        const isLocationFresh = locationCreatedAt ? moment.utc().diff(moment.utc(locationCreatedAt), 'hours') < 24 : false;
        const locationCity = rawLocation?.city ?? null;
        const locationStateCode = rawLocation?.stateCode ?? null;

        const pickupSchedule = currentLegData?.pickupDeliverySchedule;
        const deliveryTimezone = currentLegData?.consignee?.consigneeAddress?.timezone || pickupSchedule?.deliveryTimezone || null;
        const deliveryWindowType = pickupSchedule?.currentDeliveryWindowType || pickupSchedule?.initialDeliveryWindowType || 'open';
        const isDeliveryWindow = deliveryWindowType === 'ready_time' || deliveryWindowType === 'delivery_time';
        // For same-day windows the API sends currentDeliveryEndDate=null — fall back to currentDeliveryStartDate as base date
        const deliveryEndDate = pickupSchedule?.currentDeliveryEndDate || pickupSchedule?.currentDeliveryStartDate;
        const deliveryDateTime = isDeliveryWindow
            ? deliveryEndDate && pickupSchedule?.currentDeliveryEndTime
                ? `${deliveryEndDate} ${pickupSchedule.currentDeliveryEndTime}`
                : null
            : pickupSchedule?.currentDeliveryStartDate && pickupSchedule?.currentDeliveryStartTime
            ? `${pickupSchedule.currentDeliveryStartDate} ${pickupSchedule.currentDeliveryStartTime}`
            : null;
        const deliveryStartFormatted =
            isDeliveryWindow && pickupSchedule?.currentDeliveryStartDate && pickupSchedule?.currentDeliveryStartTime
                ? `${pickupSchedule.currentDeliveryStartDate} ${pickupSchedule.currentDeliveryStartTime}`
                : null;

        const destCity = currentLegData?.consignee?.consigneeAddress?.city ?? null;
        const destStateCode = currentLegData?.consignee?.consigneeAddress?.stateCode ?? null;
        const destinationText = destCity && destStateCode ? `${destCity}, ${destStateCode}` : destCity ?? null;

        const hasDeliveryTime = !!(deliveryDateTime && deliveryTimezone);
        const isDeliveryPast = hasDeliveryTime ? isDeliveryTimeInPast(deliveryDateTime, deliveryTimezone!) : false;
        const hoursToDelivery = hasDeliveryTime && !isDeliveryPast ? moment.tz(deliveryDateTime!, deliveryTimezone!).diff(moment.tz(deliveryTimezone!), 'hours') : null;
        const deliverySoon = hoursToDelivery !== null && hoursToDelivery < 24;

        const deliveryFn: DynamicFunctionConfig = {
            placeholder: '{{deliveryTime}}',
            function: getDynamicDeliveryTimeDisplay,
            args: [deliveryDateTime, deliveryTimezone, deliveryStartFormatted, isDeliveryWindow],
        };

        const hasFreshLocation = isLocationFresh && !!locationCity && !!locationStateCode;
        const hasNamedLocation = !!locationCity && !!locationStateCode;

        // Items riding this leg — "· 4 pallets" after an arrival time, "· 4 pallets on board"
        // after a location/heading phrase. Omitted entirely when not resolvable.
        const itemsArriving = nba.itemsOnBoard ? ` · ${nba.itemsOnBoard}` : '';
        const itemsOnBoard = nba.itemsOnBoard ? ` · ${nba.itemsOnBoard} on board` : '';

        // Customer-side copy (Lucas, 2026-09-05): describe the shipment, never our tracking.
        // The last ping is read as "Last seen near {city}, {age}" with a live age; when a delivery
        // time exists but is not imminent it rides along as "Expected {{deliveryTime}}".
        const lastSeenFn: DynamicFunctionConfig = { placeholder: '{{lastSeenAgo}}', function: getRelativeTimeAgo, args: [locationCreatedAt] };
        const lastSeenNear = `Last seen near ${locationCity}, ${locationStateCode}, {{lastSeenAgo}}.`;
        const expected = hasDeliveryTime ? ' Expected {{deliveryTime}}.' : '';
        const onItsWay = `Your shipment is on its way to ${destinationText || 'its destination'}.`;

        // Scheduled delivery has passed while still in transit. The title carries the scheduled time or
        // window itself (client, 2026-09-16: "should always be either a time or a window … also it's
        // Estimated") — a bare "being confirmed" next to a filled-in Delivery Time card read as if no
        // time had ever been set. getDynamicDeliveryTimeDisplay renders past values as
        // "Jul 8 by 4:00 PM (EDT)" / "Jul 8 (Between 1:00 PM-5:00 PM EDT)".
        if (isDeliveryPast && hasDeliveryTime) {
            return {
                title: { primary: 'In Transit', secondary: 'Estimated delivery {{deliveryTime}}' },
                description: 'Your shipment is still on its way and has passed its estimated delivery time. Your sales representative will confirm the updated delivery time.',
                functions: [deliveryFn],
            };
        }

        if (hasFreshLocation && deliverySoon && hasDeliveryTime) {
            return {
                title: { primary: 'In Transit', secondary: `Arriving {{deliveryTime}}${itemsArriving}` },
                description: lastSeenNear,
                functions: [deliveryFn, lastSeenFn],
            };
        }

        if (hasFreshLocation) {
            return {
                title: { primary: 'In Transit', secondary: `Near ${locationCity}, ${locationStateCode}${itemsOnBoard}` },
                description: `Last seen {{lastSeenAgo}}, on the way to ${destinationText || 'its destination'}.${expected}`,
                functions: [deliveryFn, lastSeenFn],
            };
        }

        if (deliverySoon && hasDeliveryTime && destinationText) {
            return {
                title: { primary: 'In Transit', secondary: `Arriving {{deliveryTime}}${itemsArriving}` },
                description: hasNamedLocation ? lastSeenNear : `On the way to ${destinationText}.`,
                functions: [deliveryFn, lastSeenFn],
            };
        }

        if (destinationText) {
            return {
                title: { primary: 'In Transit', secondary: `Heading to ${destinationText}${itemsOnBoard}` },
                description: `${hasNamedLocation ? lastSeenNear : onItsWay}${expected}`,
                functions: [deliveryFn, lastSeenFn],
            };
        }

        return {
            title: { primary: 'In Transit', secondary: `Your shipment is on its way${itemsOnBoard}` },
            description: hasNamedLocation ? lastSeenNear : '',
            functions: [lastSeenFn],
        };
    }

    // ── DELIVERED (also 'claim' and 'dispute': WAL-internal post-delivery states the customer
    // never sees named; their card is the delivered card, Lucas 2026-09-05) ─────────────────
    if (status === 'delivered' || status === 'claim' || status === 'dispute') {
        // POD availability is driven by the actual leg documents, not the leg's isPodUploaded flag —
        // the flag only tracks the carrier POD-collection flow and stays false when WAL uploads the POD directly.
        // Falls back to the raw leg documents for the render pass where the caller's documents state isn't populated yet.
        // Only a POD on the leg that makes THIS delivery counts (hasDeliveryPodDocument): a merged span also
        // carries the PODs of the inner legs it hides, and those never mean the order is complete.
        const isPodUploaded = hasDeliveryPodDocument({ ...currentLegData, documents: documents?.length ? documents : currentLegData?.documents });

        // A delivered leg that is NOT the order's last stop is an arrival on the way, never "Delivered".
        if (nba.isFinalStop === false) return buildIntermediateStopReached();

        // Client wording: delivered legs stay "Delivered" (only true completed/invoiced
        // statuses read "Completed").
        const deliveredPrimary = 'Delivered';
        const itemsDelivered = nba.itemsDelivered ? `${nba.itemsDelivered} delivered · ` : '';

        if (isPodUploaded) {
            return {
                title: { primary: deliveredPrimary, secondary: `${itemsDelivered}Proof of delivery ready` },
                description: 'Your order is complete and all associated documents are available for download. Thank you for your business.',
            };
        }

        return {
            title: { primary: deliveredPrimary, secondary: `${itemsDelivered}Getting your proof of delivery` },
            description: "Your shipment was delivered. We're working on getting your proof of delivery and will post it here.",
        };
    }

    // ── COMPLETED (and all post-delivery invoicing states) ───────────────────────
    if (status === 'completed' || status === 'invoice-created' || status === 'invoice-sent' || status === 'invoice-paid') {
        // WAL closes and invoices each leg on its own, so an intermediate stop can sit at `completed`
        // while the freight is still moving. "Your order is complete" is only true on the final stop;
        // any earlier page reads as the stop that was reached, like a delivered intermediate leg.
        if (nba.isFinalStop === false) return buildIntermediateStopReached();

        return {
            title: { primary: 'Completed', secondary: 'All set, documents available' },
            description: 'Your order is complete and all associated documents are available for download. Thank you for your business.',
        };
    }

    if (status === 'cancelled') {
        return {
            title: { primary: 'Order Cancelled', secondary: 'This order is no longer active' },
            description: 'This order has been cancelled.',
        };
    }

    if (status === 'draft') {
        return {
            title: { primary: 'Order Draft', secondary: 'Not yet submitted' },
            description: 'This order is in draft status.',
        };
    }

    // Safety fallback for any unmapped status — a neutral "Order Update" so a raw enum
    // (e.g. on-hold) can never leak to the customer.
    return {
        title: { primary: 'Order Update', secondary: 'Checking on the latest status' },
        description: 'View the current status of your order and important delivery milestones.',
    };
};
